export type EngagementPodMode = 'broadcast' | 'swarm';

export type EngagementPodPlatform =
	| 'twitter'
	| 'linkedin'
	| 'facebook'
	| 'threads'
	| 'reddit'
	| 'bluesky'
	| 'google_business'
	| 'tiktok';

export type UploadPostTextPlatform =
	| 'x'
	| 'linkedin'
	| 'facebook'
	| 'threads'
	| 'reddit'
	| 'bluesky'
	| 'google_business';

export type CapabilityState = 'enabled' | 'disabled' | 'blocked';

export type EngagementPodCapability = {
	platform: EngagementPodPlatform;
	label: string;
	shortLabel: string;
	icon: string;
	uploadPostPlatform: UploadPostTextPlatform | null;
	textPublishing: CapabilityState;
	broadcastTargetAcquisition: CapabilityState;
	swarmExternalReply: CapabilityState;
	firstComment: CapabilityState;
	analyticsHistory: CapabilityState;
	requiredFields: string[];
	blockedReason?: string;
};

export type UrlValidationResult =
	| {
			ok: true;
			url: string;
			username: string;
			conversationId?: string;
	  }
	| {
			ok: false;
			error: string;
	  };

export const ENGAGEMENT_POD_CAPABILITIES: Record<
	EngagementPodPlatform,
	EngagementPodCapability
> = {
	twitter: {
		platform: 'twitter',
		label: 'X',
		shortLabel: 'X',
		icon: '𝕏',
		uploadPostPlatform: 'x',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'enabled',
		swarmExternalReply: 'enabled',
		firstComment: 'enabled',
		analyticsHistory: 'enabled',
		requiredFields: ['Upload Post profile key', 'X profile URL or post URL'],
	},
	linkedin: {
		platform: 'linkedin',
		label: 'LinkedIn',
		shortLabel: 'LinkedIn',
		icon: '💼',
		uploadPostPlatform: 'linkedin',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'disabled',
		swarmExternalReply: 'disabled',
		firstComment: 'enabled',
		analyticsHistory: 'disabled',
		requiredFields: ['LinkedIn organization/page when posting as a page'],
		blockedReason:
			'LinkedIn pod Broadcast target acquisition and external replies are not implemented yet.',
	},
	facebook: {
		platform: 'facebook',
		label: 'Facebook',
		shortLabel: 'Facebook',
		icon: '📘',
		uploadPostPlatform: 'facebook',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'disabled',
		swarmExternalReply: 'disabled',
		firstComment: 'enabled',
		analyticsHistory: 'disabled',
		requiredFields: ['Facebook page ID'],
		blockedReason:
			'Facebook pod Broadcast target acquisition and external replies are not implemented yet.',
	},
	threads: {
		platform: 'threads',
		label: 'Threads',
		shortLabel: 'Threads',
		icon: '＠',
		uploadPostPlatform: 'threads',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'disabled',
		swarmExternalReply: 'disabled',
		firstComment: 'enabled',
		analyticsHistory: 'disabled',
		requiredFields: [],
		blockedReason:
			'Threads pod Broadcast target acquisition and external replies are not implemented yet.',
	},
	reddit: {
		platform: 'reddit',
		label: 'Reddit',
		shortLabel: 'Reddit',
		icon: 'R',
		uploadPostPlatform: 'reddit',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'disabled',
		swarmExternalReply: 'disabled',
		firstComment: 'enabled',
		analyticsHistory: 'disabled',
		requiredFields: ['Subreddit'],
		blockedReason:
			'Reddit pod Broadcast target acquisition and external replies are not implemented yet.',
	},
	bluesky: {
		platform: 'bluesky',
		label: 'Bluesky',
		shortLabel: 'Bluesky',
		icon: '☁',
		uploadPostPlatform: 'bluesky',
		textPublishing: 'enabled',
		broadcastTargetAcquisition: 'disabled',
		swarmExternalReply: 'disabled',
		firstComment: 'enabled',
		analyticsHistory: 'disabled',
		requiredFields: ['Bluesky target URL or AT-URI for replies'],
		blockedReason:
			'Bluesky reply docs use URL/AT-URI targets, but pod target extraction and smoke proof are not implemented yet.',
	},
	google_business: {
		platform: 'google_business',
		label: 'Google Business',
		shortLabel: 'GBP',
		icon: 'G',
		uploadPostPlatform: 'google_business',
		textPublishing: 'blocked',
		broadcastTargetAcquisition: 'blocked',
		swarmExternalReply: 'blocked',
		firstComment: 'blocked',
		analyticsHistory: 'disabled',
		requiredFields: ['Google Business location ID', 'CTA type', 'CTA URL when required'],
		blockedReason:
			'Google Business pod semantics and target acquisition are not defined for Engagement Pods.',
	},
	tiktok: {
		platform: 'tiktok',
		label: 'TikTok',
		shortLabel: 'TikTok',
		icon: 'TikTok',
		uploadPostPlatform: null,
		textPublishing: 'blocked',
		broadcastTargetAcquisition: 'blocked',
		swarmExternalReply: 'blocked',
		firstComment: 'blocked',
		analyticsHistory: 'disabled',
		requiredFields: [],
		blockedReason:
			'TikTok is not supported by the Upload Post text endpoint for Engagement Pods.',
	},
};

export const ENGAGEMENT_POD_PLATFORM_ORDER: EngagementPodPlatform[] = [
	'twitter',
	'linkedin',
	'facebook',
	'threads',
	'reddit',
	'bluesky',
	'google_business',
	'tiktok',
];

const PLATFORM_ALIASES: Record<string, EngagementPodPlatform> = {
	x: 'twitter',
	twitter: 'twitter',
	twitterx: 'twitter',
	xtwitter: 'twitter',
	linkedin: 'linkedin',
	facebook: 'facebook',
	threads: 'threads',
	reddit: 'reddit',
	bluesky: 'bluesky',
	googlebusiness: 'google_business',
	googlebusinessprofile: 'google_business',
	gbp: 'google_business',
	tiktok: 'tiktok',
	tiktokvideo: 'tiktok',
};

const normalizeKey = (value: string) =>
	String(value || '')
		.trim()
		.toLowerCase()
		.replace(/[\s/_-]+/g, '');

export function normalizeEngagementPodPlatform(
	platform: string
): EngagementPodPlatform | null {
	return PLATFORM_ALIASES[normalizeKey(platform)] || null;
}

export function getEngagementPodCapability(platform: string) {
	const normalized = normalizeEngagementPodPlatform(platform);
	return normalized ? ENGAGEMENT_POD_CAPABILITIES[normalized] : null;
}

export function isSupportedEngagementPodPlatform(platform: string): boolean {
	return !!getEngagementPodCapability(platform);
}

export function getEnabledEngagementPodPlatforms(
	mode: EngagementPodMode
): EngagementPodPlatform[] {
	return ENGAGEMENT_POD_PLATFORM_ORDER.filter((platform) => {
		const capability = ENGAGEMENT_POD_CAPABILITIES[platform];
		return mode === 'swarm'
			? capability.swarmExternalReply === 'enabled'
			: capability.textPublishing === 'enabled' &&
					capability.broadcastTargetAcquisition === 'enabled';
	});
}

export function getEngagementPodModeState(
	platform: string,
	mode: EngagementPodMode
): CapabilityState {
	const capability = getEngagementPodCapability(platform);
	if (!capability) return 'blocked';
	if (mode === 'swarm') return capability.swarmExternalReply;
	if (capability.textPublishing !== 'enabled') return capability.textPublishing;
	return capability.broadcastTargetAcquisition;
}

export function explainEngagementPodModeBlock(
	platform: string,
	mode: EngagementPodMode
): string | null {
	const capability = getEngagementPodCapability(platform);
	if (!capability) {
		return `${platform || 'This platform'} is not supported by the Engagement Pods Upload Post capability matrix.`;
	}
	const state = getEngagementPodModeState(platform, mode);
	if (state === 'enabled') return null;
	if (mode === 'swarm') {
		return (
			capability.blockedReason ||
			`${capability.label} external Swarm replies are not implemented yet.`
		);
	}
	return (
		capability.blockedReason ||
		`${capability.label} Broadcast target acquisition is not implemented yet.`
	);
}

export function assertEngagementPodModeSupported(
	platform: string,
	mode: EngagementPodMode
): EngagementPodPlatform {
	const normalized = normalizeEngagementPodPlatform(platform);
	if (!normalized) {
		throw new Error(`UNSUPPORTED_ENGAGEMENT_POD_PLATFORM:${platform}`);
	}
	const reason = explainEngagementPodModeBlock(normalized, mode);
	if (reason) {
		throw new Error(`UNSUPPORTED_ENGAGEMENT_POD_MODE:${normalized}:${mode}:${reason}`);
	}
	return normalized;
}

export function mapEngagementPodPlatformToUploadPost(
	platform: string
): UploadPostTextPlatform {
	const capability = getEngagementPodCapability(platform);
	if (!capability?.uploadPostPlatform)
		throw new Error(`UNSUPPORTED_TEXT_PLATFORM:${platform}`);
	return capability.uploadPostPlatform;
}

const parseUrl = (url: string): URL | null => {
	try {
		const parsed = new URL(url.trim());
		if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
		return parsed;
	} catch {
		return null;
	}
};

export function validateEngagementPodUrl(args: {
	url: string;
	platform: string;
	mode: EngagementPodMode;
	conversationId?: string | null;
}): UrlValidationResult {
	const normalized = normalizeEngagementPodPlatform(args.platform);
	if (!normalized) {
		return {
			ok: false,
			error: `${args.platform || 'This platform'} is not supported by Engagement Pods.`,
		};
	}
	const reason = explainEngagementPodModeBlock(normalized, args.mode);
	if (reason) return { ok: false, error: reason };

	if (normalized !== 'twitter') {
		return {
			ok: false,
			error: `${ENGAGEMENT_POD_CAPABILITIES[normalized].label} URL ingestion is disabled until target parsing is implemented.`,
		};
	}

	const parsed = parseUrl(args.url);
	if (!parsed) return { ok: false, error: 'Enter a valid http(s) URL.' };
	const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
	if (
		host !== 'x.com' &&
		host !== 'twitter.com' &&
		host !== 'mobile.twitter.com'
	) {
		return {
			ok: false,
			error: 'Enter an X profile or post URL from x.com or twitter.com.',
		};
	}
	const parts = parsed.pathname.split('/').filter(Boolean);
	const username = parts[0]?.toLowerCase();
	if (!isSafeXUsername(username)) {
		return {
			ok: false,
			error: 'The X URL must include a valid profile username.',
		};
	}
	if (args.mode === 'broadcast') {
		if (parts.length !== 1) {
			return {
				ok: false,
				error: 'Broadcast Mode requires an X profile URL like https://x.com/username.',
			};
		}
		return { ok: true, url: `https://x.com/${username}`, username };
	}

	const statusIndex = parts.findIndex(
		(part) => part.toLowerCase() === 'status'
	);
	const conversationId =
		statusIndex >= 0 ? parts[statusIndex + 1] : undefined;
	if (!conversationId || !/^\d+$/.test(conversationId)) {
		return {
			ok: false,
			error: 'Swarm Mode requires an X post URL like https://x.com/username/status/123456.',
		};
	}
	if (args.conversationId && args.conversationId !== conversationId) {
		return {
			ok: false,
			error:
				'Provided conversationId does not match the X status ID in the URL.',
		};
	}
	return {
		ok: true,
		url: `https://x.com/${username}/status/${conversationId}`,
		username,
		conversationId,
	};
}

const RESERVED_X_PATHS = new Set([
	'home',
	'explore',
	'notifications',
	'messages',
	'i',
	'intent',
	'share',
	'search',
	'settings',
	'login',
	'logout',
	'oauth',
]);

function isSafeXUsername(username?: string) {
	if (!username) return false;
	if (RESERVED_X_PATHS.has(username)) return false;
	return /^[a-z0-9_]{1,15}$/i.test(username);
}
